/** Política de senha forte, espelho de `backend/app/services/password_policy.py`.
 *
 *  A regra vive nos dois lados porque a interface precisa avaliar enquanto a
 *  pessoa digita, e o backend nunca pode confiar no cliente. O que impede os
 *  dois de divergirem em silêncio é a lista comum de casos:
 *  `passwordPolicy.test.ts` aqui e `tests/unit/test_password_policy.py` lá usam
 *  os mesmos exemplos.
 *
 *  Se mudar algo aqui, mude no Python também. Os valores abaixo acompanham os
 *  defaults de PASSWORD_MIN_LENGTH e PASSWORD_REQUIRED_CLASSES.
 */

export const COMPRIMENTO_MINIMO = 12
export const CLASSES_EXIGIDAS = 3

export type ChaveDeClasse = 'maiuscula' | 'minuscula' | 'numero' | 'simbolo'

const CLASSES: { chave: ChaveDeClasse; teste: RegExp }[] = [
  { chave: 'maiuscula', teste: /[A-ZÀ-ÖØ-Þ]/ },
  { chave: 'minuscula', teste: /[a-zß-öø-ÿ]/ },
  { chave: 'numero', teste: /[0-9]/ },
  { chave: 'simbolo', teste: /[^A-Za-zÀ-ÿ0-9]/ },
]

const TERMOS_PREVISIVEIS = [
  '123456', '1234567', '12345678', '123456789',
  'senha', 'password', 'qwerty', 'asdfgh', 'abcdef',
  'admin', 'master', 'acesso', 'mudar123', 'trocar123',
  'transgraos', 'hubtransgraos', 'ordinum', 'graos',
]

/** Motivo da recusa. A tela traduz; o módulo não conhece idioma. */
export type MotivoDeRecusa = 'curta' | 'classes' | 'previsivel' | 'dados-pessoais'

export type NivelDeForca = 'fraca' | 'media' | 'forte'

export interface AvaliacaoDeSenha {
  valida: boolean
  motivo: MotivoDeRecusa | null
  nivel: NivelDeForca
  comprimentoOk: boolean
  classes: ChaveDeClasse[]
}

export interface DadosDoDono {
  email?: string | null
  nome?: string | null
}

function semAcento(texto: string): string {
  // Faixa dos diacríticos combinantes, escrita em escape para o arquivo não
  // depender de caracteres invisíveis.
  return texto.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
}

function normalizar(texto: string): string {
  return semAcento(texto ?? '').toLowerCase()
}

export function classesAtendidas(senha: string): ChaveDeClasse[] {
  return CLASSES.filter(({ teste }) => teste.test(senha ?? '')).map(({ chave }) => chave)
}

/** Trechos do dono que não podem aparecer na senha. Só de 4 caracteres em
 *  diante: abaixo disso, um nome curto barraria senhas legítimas. */
function pedacosDoDono({ email, nome }: DadosDoDono): string[] {
  const pedacos: string[] = []
  if (email) {
    const local = normalizar(email).split('@')[0]
    pedacos.push(...local.split(/[._\-+]/).filter((p) => p.length >= 4))
  }
  if (nome) {
    pedacos.push(...normalizar(nome).split(/\s+/).filter((p) => p.length >= 4))
  }
  return pedacos
}

export function avaliar(senha: string, dono: DadosDoDono = {}): AvaliacaoDeSenha {
  const valor = senha ?? ''
  const classes = classesAtendidas(valor)
  const comprimentoOk = valor.length >= COMPRIMENTO_MINIMO
  const base: AvaliacaoDeSenha = { valida: false, motivo: null, nivel: 'fraca', comprimentoOk, classes }

  if (!valor) return base
  if (!comprimentoOk) return { ...base, motivo: 'curta' }
  if (classes.length < CLASSES_EXIGIDAS) return { ...base, motivo: 'classes', nivel: 'fraca' }

  const normalizada = normalizar(valor)
  if (TERMOS_PREVISIVEIS.some((termo) => normalizada.includes(termo))) {
    return { ...base, motivo: 'previsivel' }
  }
  if (pedacosDoDono(dono).some((pedaco) => normalizada.includes(pedaco))) {
    return { ...base, motivo: 'dados-pessoais' }
  }

  // Passou na regra. O nível separa o que atende do que é confortavelmente
  // seguro, para o medidor ter o que dizer além de aprovado.
  const nivel: NivelDeForca =
    valor.length >= 16 && classes.length === 4 ? 'forte' : 'media'
  return { valida: true, motivo: null, nivel, comprimentoOk, classes }
}

/** Atalho para validações de formulário. */
export function senhaValida(senha: string, dono: DadosDoDono = {}): boolean {
  return avaliar(senha, dono).valida
}
